/**
 * VH-126: Training Locations API client.
 *
 * Wraps /api/v1/training-locations/* and /api/v1/scans/*. Uses the shared
 * axios instance so auth, FormData handling and 401 redirects behave the same
 * as everywhere else.
 */

import { api } from './api'

// Mirrors the server-side ceilings in equipment_scan_service.py. Enforced here
// too so the user is told before a 10 MB upload rather than after it.
export const MAX_PHOTOS_PER_SCAN = 30
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
]

function photoForm(file) {
  const form = new FormData()
  form.append('file', file)
  return form
}

export const trainingLocationsAPI = {
  // -- Places ---------------------------------------------------------
  list: () => api.get('/api/v1/training-locations'),
  get: (id) => api.get(`/api/v1/training-locations/${id}`),
  // VH-126 Stage C: what can be trained here, and which machines are idle.
  coverage: (id) => api.get(`/api/v1/training-locations/${id}/coverage`),
  // VH-126 Stage C2: what could be brought over from the user's other places.
  carryOverOffers: (id) => api.get(`/api/v1/training-locations/${id}/carry-over`),
  applyCarryOver: (id, exerciseIds) =>
    api.post(`/api/v1/training-locations/${id}/carry-over`, { exercise_ids: exerciseIds }),
  create: (data) => api.post('/api/v1/training-locations', data),
  update: (id, data) => api.put(`/api/v1/training-locations/${id}`, data),
  remove: (id) => api.delete(`/api/v1/training-locations/${id}`),

  // -- The fast path: one photo, one machine, answered in the request --
  quickScan: (locationId, file) =>
    api.post(`/api/v1/training-locations/${locationId}/quick-scan`, photoForm(file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // -- Many photos, added one at a time to an open scan ----------------
  addPhoto: (scanId, file) =>
    api.post(`/api/v1/scans/${scanId}/media`, photoForm(file), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getScan: (scanId) => api.get(`/api/v1/scans/${scanId}`),

  // -- The review gate ------------------------------------------------
  confirmScan: (scanId, acceptedIds, nameCorrections = {}) =>
    api.post(`/api/v1/scans/${scanId}/confirm`, {
      accepted_candidate_ids: acceptedIds,
      name_corrections: nameCorrections,
    }),
  discardScan: (scanId) => api.delete(`/api/v1/scans/${scanId}`),
}

/**
 * Check a file before it costs the user an upload.
 *
 * Returns null when the file is fine, or a translation key plus its
 * interpolation values when it is not — the caller owns the wording so this
 * stays usable from anywhere.
 */
export function validatePhoto(file) {
  if (!file) return { key: 'trainingLocations.errors.noFile', values: {} }
  if (file.type && !ACCEPTED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
    return { key: 'trainingLocations.errors.badType', values: { name: file.name } }
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return { key: 'trainingLocations.errors.tooLarge', values: { name: file.name } }
  }
  return null
}
